import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Currency from "../../common/Currency";
import { adminOrderItemService } from "../../../services/adminOrderItemService";
import {
  OrderItemDetail,
  OrderItemHistoryEntry,
  PaginatedHistoryResponse,
} from "../../../types/admin";
import { formatDate } from "../../../utils/formatters";
import { getStatusColor } from "../../../utils/statusColors";

interface OrderItemDetailsModalProps {
  open: boolean;
  onClose: () => void;
  orderItemId: string;
}

const OrderItemDetailsModal: React.FC<OrderItemDetailsModalProps> = ({
  open,
  onClose,
  orderItemId,
}) => {
  const { t } = useTranslation();
  const [orderItem, setOrderItem] = useState<OrderItemDetail | null>(null);
  const [orderItemHistory, setOrderItemHistory] = useState<
    OrderItemHistoryEntry[]
  >([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyPagination, setHistoryPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    hasMore: false,
    totalCount: 0,
  });

  const loadOrderItemDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const details =
        await adminOrderItemService.getOrderItemDetail(orderItemId);
      setOrderItem(details);
      // Reset history and pagination when loading new item
      setOrderItemHistory([]);
      setHistoryPagination({
        currentPage: 1,
        totalPages: 0,
        hasMore: false,
        totalCount: 0,
      });
      setHistoryExpanded(false);
    } catch (err) {
      setError(t("admin.orders.failedToLoadItemDetails"));
      console.error("Error loading order item details:", err);
    } finally {
      setLoading(false);
    }
  }, [orderItemId]);

  useEffect(() => {
    if (open && orderItemId) {
      loadOrderItemDetails();
    }
  }, [open, orderItemId, loadOrderItemDetails]);

  const loadOrderItemHistory = async (loadMore = false) => {
    if (!orderItem?.id) return; // Need order item ID to load history

    // If loading more, don't load if already loading or no more pages
    if (loadMore && (historyLoading || !historyPagination.hasMore)) return;

    // If not loading more and already have data, don't reload
    if (!loadMore && orderItemHistory.length > 0) return;

    setHistoryLoading(true);
    try {
      const pageToLoad = loadMore ? historyPagination.currentPage + 1 : 1;
      const historyResponse: PaginatedHistoryResponse =
        await adminOrderItemService.getOrderItemHistory(
          orderItem.id,
          pageToLoad,
          20,
        );

      if (loadMore) {
        // Append new records to existing ones
        setOrderItemHistory((prev) => [...prev, ...historyResponse.items]);
      } else {
        // Replace with new records
        setOrderItemHistory(historyResponse.items);
      }

      // Update pagination info
      setHistoryPagination({
        currentPage: historyResponse.pageNumber,
        totalPages: historyResponse.totalPages,
        hasMore: historyResponse.hasNextPage,
        totalCount: historyResponse.totalCount,
      });
    } catch (err) {
      console.error("Error loading order item history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryToggle = () => {
    setHistoryExpanded(!historyExpanded);
    if (!historyExpanded) {
      loadOrderItemHistory();
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  if (!orderItem) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="info">{t("admin.orders.noItemDetailsFound")}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t("admin.orders.orderItemDetails")} - {orderItem.title}
          </Typography>
          <Box display="flex" gap={1}>
            <Chip
              label={
                orderItem.itemStatusDisplayName ||
                orderItem.itemStatusName ||
                "Unknown"
              }
              color={getStatusColor(orderItem.itemStatusName || "Unknown")}
              size="small"
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Order Item Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("admin.orders.itemInformation")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.itemId")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderItem.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.productId")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderItem.productId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.orderId")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderItem.orderId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.statusId")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.itemStatusId || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.price")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      <Currency amount={orderItem.price} tjsAmount={orderItem.priceTjs} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.quantity")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.quantity}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.subtotal")}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="primary"
                    >
                      <Currency
                        amount={orderItem.price * orderItem.quantity}
                        tjsAmount={orderItem.priceTjs ? orderItem.priceTjs * orderItem.quantity : null}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.retailer")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.retailer || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.retailerOrderId")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderItem.retailerOrderId || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.retailerTrackingId")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderItem.retailerTrackingId || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.trackingNumber")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderItem.trackingNumber || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.flightNumber")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderItem.flightNumber || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.estimatedDelivery")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.estimatedDelivery
                        ? formatDate(orderItem.estimatedDelivery)
                        : t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.orderedAt")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.orderedAt
                        ? formatDate(orderItem.orderedAt)
                        : t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.shippedAt")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.shippedAt
                        ? formatDate(orderItem.shippedAt)
                        : t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.estimatedWeight")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.estimatedWeight !== undefined && orderItem.estimatedWeight !== null
                        ? `${orderItem.estimatedWeight.toFixed(2)} kg`
                        : t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.cargoWeight")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.cargoWeight !== undefined && orderItem.cargoWeight !== null
                        ? `${orderItem.cargoWeight.toFixed(2)} kg`
                        : t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.cargoPrice")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {orderItem.cargoPrice !== undefined &&
                      orderItem.cargoPrice !== null ? (
                        <Currency amount={orderItem.cargoPrice} tjsAmount={orderItem.cargoPriceTjs} />
                      ) : (
                        t("admin.orders.notSet")
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.itemCarrierCargoFee")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {orderItem.itemCarrierCargoFee !== undefined &&
                      orderItem.itemCarrierCargoFee !== null ? (
                        <Currency amount={orderItem.itemCarrierCargoFee} />
                      ) : (
                        t("admin.orders.notSet")
                      )}
                    </Typography>
                  </Grid>

                  {/* Seller Info Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                      {t("admin.orders.sellerInfo")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.sellerName")}
                    </Typography>
                    <Typography variant="body1">
                      {orderItem.sellerName || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.sellerId")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderItem.sellerId || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.offerId")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace" sx={{ wordBreak: "break-all" }}>
                      {orderItem.offerId || t("admin.orders.notSet")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orders.sellerShippingFee")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {orderItem.sellerShippingFee != null ? (
                        <Currency amount={orderItem.sellerShippingFee} tjsAmount={orderItem.sellerShippingFeeTjs} />
                      ) : (
                        t("admin.orders.notSet")
                      )}
                    </Typography>
                  </Grid>

                  {orderItem.notes && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.orders.notes")}
                      </Typography>
                      <Typography variant="body1">{orderItem.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Order Item History - Expandable Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  sx={{
                    p: 2,
                    bgcolor: "primary.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "primary.200",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <HistoryIcon color="primary" />
                    <Typography variant="h6" color="primary">
                      {t("admin.orders.itemHistory")}
                    </Typography>
                    {orderItemHistory.length > 0 && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          bgcolor: "primary.100",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {t("admin.orders.historyEntryCount", { count: orderItemHistory.length })}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    onClick={handleHistoryToggle}
                    aria-expanded={historyExpanded}
                    aria-label="show more"
                    color="primary"
                  >
                    {historyExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={historyExpanded}>
                  {historyLoading && orderItemHistory.length === 0 ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : orderItemHistory.length === 0 ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      py={4}
                      sx={{ color: "text.secondary" }}
                    >
                      <HistoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" gutterBottom>
                        {t("admin.orders.noHistoryAvailable")}
                      </Typography>
                      <Typography variant="body2" textAlign="center">
                        {t("admin.orders.noHistoryDescription")}
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      {/* History List */}
                      <List>
                        {orderItemHistory.map((entry, index) => (
                          <React.Fragment key={entry.id}>
                            <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                              <ListItemIcon sx={{ mt: 1 }}>
                                <HistoryIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                  >
                                    <Typography
                                      variant="h6"
                                      fontWeight="medium"
                                      color="primary"
                                    >
                                      {entry.action}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                      sx={{
                                        bgcolor: "grey.100",
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {formatDate(entry.createdAt)}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      color="textPrimary"
                                      paragraph
                                      sx={{ mb: 1 }}
                                    >
                                      {entry.description}
                                    </Typography>

                                    <Box
                                      sx={{ display: "flex", gap: 2, mb: 1 }}
                                    >
                                      {entry.performedBy && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                          >
                                            👤
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                          >
                                            {entry.performedBy}
                                          </Typography>
                                        </Box>
                                      )}
                                      {entry.note && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                          >
                                            📝
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                          >
                                            {entry.note}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                    {(entry.oldValuesJson ||
                                      entry.newValuesJson) && (
                                      <Box mt={2}>
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                          color="primary"
                                          gutterBottom
                                        >
                                          {t("admin.orders.changesMade")}
                                        </Typography>
                                        {(() => {
                                          try {
                                            const oldValues =
                                              entry.oldValuesJson
                                                ? JSON.parse(
                                                    entry.oldValuesJson,
                                                  )
                                                : {};
                                            const newValues =
                                              entry.newValuesJson
                                                ? JSON.parse(
                                                    entry.newValuesJson,
                                                  )
                                                : {};

                                            // Get all keys from both old and new values
                                            const allKeys = new Set([
                                              ...Object.keys(oldValues),
                                              ...Object.keys(newValues),
                                            ]);

                                            return Array.from(allKeys)
                                              .map((key) => {
                                                const oldValue = oldValues[key];
                                                const newValue = newValues[key];

                                                // Skip if no change
                                                if (oldValue === newValue)
                                                  return null;

                                                return (
                                                  <Box
                                                    key={key}
                                                    sx={{
                                                      mb: 1,
                                                      p: 1,
                                                      bgcolor: "grey.50",
                                                      borderRadius: 1,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="body2"
                                                      fontWeight="medium"
                                                      color="textSecondary"
                                                    >
                                                      {key
                                                        .replace(
                                                          /([A-Z])/g,
                                                          " $1",
                                                        )
                                                        .replace(/^./, (str) =>
                                                          str.toUpperCase(),
                                                        )}
                                                      :
                                                    </Typography>
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        mt: 0.5,
                                                      }}
                                                    >
                                                      <Typography
                                                        variant="body2"
                                                        sx={{
                                                          color: "error.main",
                                                          textDecoration:
                                                            "line-through",
                                                          fontStyle: "italic",
                                                        }}
                                                      >
                                                        {oldValue || t("admin.orders.empty")}
                                                      </Typography>
                                                      <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                      >
                                                        →
                                                      </Typography>
                                                      <Typography
                                                        variant="body2"
                                                        sx={{
                                                          color: "success.main",
                                                          fontWeight: "medium",
                                                        }}
                                                      >
                                                        {newValue || t("admin.orders.empty")}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                );
                                              })
                                              .filter(Boolean);
                                          } catch (error) {
                                            return (
                                              <Typography
                                                variant="body2"
                                                color="error"
                                              >
                                                {t("admin.orders.errorParsingChanges")}
                                              </Typography>
                                            );
                                          }
                                        })()}
                                      </Box>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index < orderItemHistory.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>

                      {/* Pagination Info and Load More Button */}
                      <Box
                        mt={2}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="textSecondary">
                          {t("admin.orders.showingXofYRecords", { shown: orderItemHistory.length, total: historyPagination.totalCount })}
                          {historyPagination.totalPages > 1 && (
                            <span>
                              {" "}
                              ({t("admin.orders.pageXofY", { current: historyPagination.currentPage, total: historyPagination.totalPages })})
                            </span>
                          )}
                        </Typography>

                        {historyPagination.hasMore && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => loadOrderItemHistory(true)}
                            disabled={historyLoading}
                            startIcon={
                              historyLoading ? (
                                <CircularProgress size={16} />
                              ) : null
                            }
                          >
                            {historyLoading ? t("common.loading") : t("admin.loadMore")}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderItemDetailsModal;
